<template>
  <div class="occupation-field">
    <FormSelect
      name="occupationDropdown"
      type="text"
      :items="occupationList"
      :label="$t('FormSelect.label.occupationDropdown')"
      :placeholder="$t('FormSelect.placeholder.occupation')"
      @dropdownValueUpdate="updateOccupationDropdown"
    />

    <FormInputField
      v-if="showFieldToEnterOccupation"
      type="text"
      name="occupationOther"
      :label="$t('FormInputField.label.occupationOther')"
      :placeholder="$t('FormInputField.placeholder.occupationOther')"
      :inputValue="occupationData.occupationOther"
      :errors="occupationStore.getErrorByField('occupationOther')"
      v-model:occupationOther="occupationData.occupationOther"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import useValidate from "@vuelidate/core";
import { required, helpers, requiredIf } from "@vuelidate/validators";
import { useI18n } from "vue-i18n";

import FormSelect from "@/components/ui/selects/FormSelect.vue";
import FormInputField from "@/components/ui/inputs/FormInputField.vue";
import { useOccupationStore } from "@/stores/forms/occupationFormStore";

const emit = defineEmits(["inputValueUpdate"]);
const occupationStore = useOccupationStore();
const { t } = useI18n();

const occupationList = [
  "Accounting",
  "Agriculture",
  "Administration",
  "Arts",
  "Banking",
  "Business Management",
  "Computers or Information Technology",
  "Construction",
  "Education & Training",
  "Entertainment",
  "Finance",
  "Government Official",
  "Healthcare",
  "Hospitality",
  "Human Resources",
  "Insurance",
  "Law",
  "Manufacturing",
  "Marketing",
  "Mining",
  "Media",
  "Other",
  "Real Estate",
  "Retired",
  "Sales",
  "Science",
  "Sports",
  "Student",
  "Transportation",
  "Unemployed",
];

const occupationData = ref({
  occupationDropdown: occupationStore.form.occupationDropdown.value || "",
  occupationOther: occupationStore.form.occupationOther.value || "",
});

// Only characters, space or underscore is allowed
const mustNotContainSpecialCharacters = (value) =>
  /^[A-Za-z0-9 _]*$/.test(value);

// Displaying a field to specify the occupation only if a user has selected 'Other' as an Occupation
const showFieldToEnterOccupation = computed(() => {
  const hasOtherSelected = occupationData.value.occupationDropdown === "Other";
  return hasOtherSelected;
});

const rules = computed(() => {
  return {
    occupationDropdown: { required },
    occupationOther: {
      required: helpers.withMessage(
        t("FormInputField.error.requiredErrorMsg"),
        requiredIf(showFieldToEnterOccupation)
      ),
      mustNotContainSpecialCharacters: helpers.withMessage(
        t("FormInputField.error.mustNotContainSpecialCharsErrorMsg"),
        mustNotContainSpecialCharacters
      ),
    },
  };
});

const v$ = useValidate(rules, occupationData);

// Updating Occuptaion dropdown value
const updateOccupationDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  occupationData.value[field] = value;

  occupationStore.updateValue(field, value);

  // Updating Store and pass the value to parent component
  updateOccupationAndEmit();
};

// Composing the value of occupation
const occupation = computed(() =>
  showFieldToEnterOccupation.value
    ? `Other - ${occupationData.value.occupationOther}`
    : occupationData.value.occupationDropdown
);

const updateOccupationAndEmit = () => {
  const validation = v$.value;
  validation.$validate();

  // Updating errors in Occupation store
  const occupationOtherErrors = validation.occupationOther.$error
    ? validation.occupationOther.$errors[0].$message
    : "";
  const occupationDropdownErrors = validation.occupationDropdown.$error
    ? validation.occupationDropdown.$errors[0].$message
    : "";

  occupationStore.updateErrorFieldByKey(
    "occupationOther",
    occupationOtherErrors
  );
  occupationStore.updateErrorFieldByKey(
    "occupationDropdown",
    occupationDropdownErrors
  );

  occupationStore.updateValue("occupation", occupation.value);
  emit("inputValueUpdate", {
    occupation: occupation.value,
  });
};

watch(
  () => occupationData.value.occupationOther,
  (occupationOther) => {
    const trimmedValue = occupationOther.trim();
    // Updating value in the store
    occupationStore.updateValue("occupationOther", trimmedValue);
    // Update Occupation value in store and pass the value to parent component
    updateOccupationAndEmit();
  }
);
</script>
